'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from '@/shared/navigation'
import {
  fetchTodayWarehouseMaterialReturns,
  publishWarehouseMaterialReturn,
  saveWarehouseMaterialReturn,
  type ApiWarehouseMaterialReturn,
  type ApiWarehouseMaterialReturnLine,
  type WarehouseMaterialReturnLineInput,
} from '@/features/kitchen/api/warehouseMaterialReturnApi'
import { fetchKitchenMaterialStock } from '@/features/kitchen/api/kitchenTransferApi'
import { shouldUseKitchenApi } from '@/features/kitchen/api/kitchenApiMode'
import { kitchenTerms } from '@/features/kitchen/kitchenTerms'
import { EmptyState } from '@/shared/components/EmptyState'
import { FlowStepper } from '@/shared/components/FlowStepper'
import { QtyStepper } from '@/shared/components/QtyStepper'
import { QrDisplay } from '@/shared/components/QrDisplay'
import { SkeletonList } from '@/shared/components/Skeleton'
import { StickyActionFooter } from '@/shared/components/StickyActionFooter'
import { Toast } from '@/shared/components/Toast'
import { getKitchenDevice } from '@/shared/auth/deviceStorage'
import { getKitchenSession } from '@/shared/auth/sessionStorage'
import { canPerformHeadActions } from '@/shared/auth/canPerformHeadActions'
import { qrCountdownLabel } from '@/shared/utils/qrCountdown'

type View = 'list' | 'edit' | 'review' | 'qr'

type EditorLine = {
  materialId: string
  materialName: string
  uom: string
  qty: number
  maxQty: number
}

type QrState = {
  payload: string
  expiresAt: string
  returnCode: string
  warehouseName: string
}

const FLOW_STEPS = kitchenTerms.warehouseReturn.flowSteps.map((label) => ({ label }))

function flowStep(view: View): number {
  if (view === 'qr') return 4
  if (view === 'review') return 3
  if (view === 'edit') return 2
  return 1
}

function statusLabel(status: string): string {
  if (status === 'draft') return kitchenTerms.warehouseReturn.status.draft
  if (status === 'ready_for_pickup') return kitchenTerms.warehouseReturn.status.readyForPickup
  if (status === 'picked_up') return kitchenTerms.warehouseReturn.status.pickedUp
  if (status === 'validated') return kitchenTerms.warehouseReturn.status.validated
  return status
}

function statusBadge(status: string): string {
  if (status === 'draft') return 'badge-draft'
  if (status === 'ready_for_pickup') return 'badge-warning'
  if (status === 'picked_up') return 'badge-warning'
  if (status === 'validated') return 'badge-success'
  return 'badge-draft'
}

function summaryText(lines: ApiWarehouseMaterialReturnLine[]): string {
  const total = lines.reduce((sum, line) => sum + line.qty_kitchen, 0)
  return kitchenTerms.warehouseReturn.summary.join([
    kitchenTerms.warehouseReturn.summary.materialCount(lines.length),
    `${total} qty`,
  ])
}

function filterEditableLines(lines: EditorLine[], showAll: boolean): EditorLine[] {
  if (showAll) return lines
  const withStock = lines.filter((line) => line.maxQty > 0 || line.qty > 0)
  return withStock.length > 0 ? withStock : lines.filter((line) => line.qty > 0)
}

export function WarehouseReturnPage() {
  const device = getKitchenDevice()
  const session = getKitchenSession()
  const useApi = shouldUseKitchenApi()
  const kitchenName = device?.kitchenName ?? 'Dapur'
  const canPerformHeadActionsForSession = session ? canPerformHeadActions(session) : false

  const [view, setView] = useState<View>('list')
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [returns, setReturns] = useState<ApiWarehouseMaterialReturn[]>([])
  const [materialStock, setMaterialStock] = useState<
    { materialName: string; uom: string; stockAvailable: number }[]
  >([])

  const [returnRef, setReturnRef] = useState('new')
  const [returnCode, setReturnCode] = useState('')
  const [warehouseName, setWarehouseName] = useState('Gudang')
  const [notes, setNotes] = useState('')
  const [editorLines, setEditorLines] = useState<EditorLine[]>([])
  const [showAllLines, setShowAllLines] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const [qrState, setQrState] = useState<QrState | null>(null)
  const [nowMs, setNowMs] = useState(() => Date.now())
  const [toast, setToast] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isPublishing, setIsPublishing] = useState(false)

  const todayLabel = useMemo(
    () =>
      new Intl.DateTimeFormat('id-ID', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }).format(new Date()),
    [],
  )

  const reload = useCallback(async () => {
    if (!device?.deviceId || !canPerformHeadActionsForSession) {
      setLoading(false)
      return
    }
    setLoading(true)
    setLoadError(null)
    try {
      const [returnsRes, stock] = await Promise.all([
        fetchTodayWarehouseMaterialReturns(device.deviceId),
        fetchKitchenMaterialStock(device.deviceId),
      ])
      setReturns(returnsRes.returns)
      setMaterialStock(stock)
    } catch {
      setLoadError(kitchenTerms.warehouseReturn.loadError)
    } finally {
      setLoading(false)
    }
  }, [device?.deviceId, canPerformHeadActionsForSession])

  useEffect(() => {
    void reload()
  }, [reload, refreshKey])

  useEffect(() => {
    if (!device?.deviceId || !canPerformHeadActionsForSession) return undefined
    const hasPending = returns.some(
      (item) => item.status === 'draft' || item.status === 'ready_for_pickup',
    )
    if (!hasPending) return undefined
    const timer = window.setInterval(() => void reload(), 5_000)
    return () => window.clearInterval(timer)
  }, [device?.deviceId, canPerformHeadActionsForSession, returns, reload])

  useEffect(() => {
    if (view !== 'qr') return undefined
    const timer = window.setInterval(() => setNowMs(Date.now()), 1_000)
    return () => window.clearInterval(timer)
  }, [view])

  const draftReturns = useMemo(() => returns.filter((item) => item.status === 'draft'), [returns])
  const publishedReturns = useMemo(() => returns.filter((item) => item.status !== 'draft'), [returns])

  const visibleLines = useMemo(
    () => filterEditableLines(editorLines, showAllLines),
    [editorLines, showAllLines],
  )

  const payloadLines = useMemo((): WarehouseMaterialReturnLineInput[] => {
    return editorLines
      .filter((line) => line.qty > 0)
      .map((line) => ({
        materialId: line.materialId || undefined,
        materialCode: line.materialName,
        qty: line.qty,
        uom: line.uom,
      }))
  }, [editorLines])

  const payloadTotalQty = useMemo(
    () => payloadLines.reduce((sum, line) => sum + line.qty, 0),
    [payloadLines],
  )

  function buildEditorLinesFromStock(existingLines: ApiWarehouseMaterialReturnLine[] = []) {
    if (existingLines.length > 0) {
      setEditorLines(
        existingLines.map((line) => {
          const lot = materialStock.find(
            (s) => s.materialName === line.material_name && s.uom === line.uom,
          )
          return {
            materialId: line.material_id,
            materialName: line.material_name,
            uom: line.uom,
            qty: line.qty_kitchen,
            maxQty: lot?.stockAvailable ?? line.qty_kitchen,
          }
        }),
      )
      return
    }
    setEditorLines(
      materialStock.map((lot) => ({
        materialId: '',
        materialName: lot.materialName,
        uom: lot.uom,
        qty: 0,
        maxQty: lot.stockAvailable,
      })),
    )
  }

  function startNewReturn() {
    setReturnRef('new')
    setReturnCode('')
    setWarehouseName(returns[0]?.warehouse_name ?? 'Gudang Pusat')
    setNotes('')
    buildEditorLinesFromStock()
    setShowAllLines(false)
    setFormError(null)
    setView('edit')
  }

  function openReturnEdit(item: ApiWarehouseMaterialReturn) {
    setReturnRef(item.id)
    setReturnCode(item.code)
    setWarehouseName(item.warehouse_name)
    setNotes(item.notes ?? '')
    buildEditorLinesFromStock(item.lines)
    setShowAllLines(false)
    setFormError(null)
    setView('edit')
  }

  function validateForm(): boolean {
    if (payloadLines.length === 0) {
      setFormError(kitchenTerms.warehouseReturn.toast.minOneLine)
      return false
    }
    setFormError(null)
    return true
  }

  function openReview() {
    if (!validateForm()) return
    setView('review')
  }

  async function handleSaveDraft(): Promise<boolean> {
    if (!device?.deviceId || !validateForm()) return false
    setIsSaving(true)
    try {
      const res = await saveWarehouseMaterialReturn(returnRef, { notes, lines: payloadLines })
      setReturnRef(res.return_id)
      setReturnCode(res.return_code)
      setToast(kitchenTerms.warehouseReturn.toast.draftSaved)
      await reload()
      return true
    } catch (e) {
      setToast(e instanceof Error ? e.message : kitchenTerms.warehouseReturn.toast.saveFailed)
      return false
    } finally {
      setIsSaving(false)
    }
  }

  async function handlePublish() {
    if (!device?.deviceId || !validateForm()) return
    setIsPublishing(true)
    try {
      const saved = await saveWarehouseMaterialReturn(returnRef, { notes, lines: payloadLines })
      const ref = saved.return_id
      setReturnRef(ref)
      setReturnCode(saved.return_code)

      const res = await publishWarehouseMaterialReturn(ref)
      setQrState({
        payload: res.payload,
        expiresAt: res.expires_at,
        returnCode: res.return_code,
        warehouseName,
      })
      setToast(kitchenTerms.warehouseReturn.qr.publishedToast)
      setView('qr')
      await reload()
    } catch (e) {
      setToast(e instanceof Error ? e.message : kitchenTerms.warehouseReturn.toast.publishFailed)
    } finally {
      setIsPublishing(false)
    }
  }

  const qrExpired = qrState ? new Date(qrState.expiresAt).getTime() <= nowMs : false
  const qrTtlLabel = qrState
    ? qrCountdownLabel(
        qrState.expiresAt,
        nowMs,
        kitchenTerms.warehouseReturn.qr.expired,
        kitchenTerms.warehouseReturn.qr.remaining,
      )
    : ''

  if (!canPerformHeadActionsForSession) {
    return (
      <section className="space-y-4 pb-24">
        <div className="card space-y-3">
          <Link href="/kitchen/home" className="text-body-sm text-primary inline-flex items-center gap-1">
            <span className="material-symbols-outlined text-[18px]">arrow_back</span>
            {kitchenTerms.warehouseReturn.backHome}
          </Link>
          <p className="text-headline-md text-primary break-words">{kitchenTerms.warehouseReturn.title}</p>
        </div>
        <EmptyState
          icon="lock"
          title={kitchenTerms.warehouseReturn.forbidden.title}
          description={kitchenTerms.warehouseReturn.forbidden.description}
        />
      </section>
    )
  }

  return (
    <section className="space-y-4 pb-24">
      <div className="card relative space-y-3">
        <button
          type="button"
          className="btn-outline absolute top-3 right-3 min-w-[44px] px-3 z-10"
          onClick={() => setRefreshKey((k) => k + 1)}
          aria-label={kitchenTerms.warehouseReturn.refreshAria}
          title={kitchenTerms.warehouseReturn.refreshAria}
        >
          <span className="material-symbols-outlined">refresh</span>
        </button>

        <Link href="/kitchen/home" className="text-body-sm text-primary inline-flex items-center gap-1 pr-12">
          <span className="material-symbols-outlined text-[18px]">arrow_back</span>
          {kitchenTerms.warehouseReturn.backHome}
        </Link>
        <p className="text-headline-md text-primary break-words pr-12">{kitchenTerms.warehouseReturn.title}</p>
        <p className="text-body-sm text-on-surface-variant">{kitchenTerms.warehouseReturn.subtitle}</p>
        <p className="text-body-sm text-on-surface-variant capitalize">
          {kitchenName} · {todayLabel}
        </p>
        <div className="pt-3 border-t border-surface-variant">
          <FlowStepper steps={FLOW_STEPS} activeStep={flowStep(view)} />
        </div>
      </div>

      {loading && <SkeletonList count={3} label={kitchenTerms.warehouseReturn.loading} />}

      {loadError && (
        <div role="alert" className="alert-error flex flex-wrap items-center justify-between gap-2">
          <span className="text-body-sm">{loadError}</span>
          <button
            type="button"
            className="btn-outline min-h-[36px] text-body-sm"
            onClick={() => setRefreshKey((k) => k + 1)}
          >
            {kitchenTerms.warehouseReturn.retry}
          </button>
        </div>
      )}

      {!loading && !loadError && view === 'list' && (
        <div className="space-y-4">
          {draftReturns.length > 0 && (
            <div className="space-y-2">
              <p className="text-body-sm font-semibold text-on-surface-variant">
                {kitchenTerms.warehouseReturn.list.draftSection}
              </p>
              {draftReturns.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="card w-full text-left hover:border-primary transition-colors"
                  onClick={() => openReturnEdit(item)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-body-lg font-semibold text-on-surface break-words">
                        {kitchenTerms.warehouseReturn.list.toWarehouse(item.warehouse_name)}
                      </p>
                      <p className="text-body-sm text-on-surface-variant mt-1">{summaryText(item.lines)}</p>
                      <p className="text-label-md font-mono text-on-surface-variant mt-1">{item.code}</p>
                    </div>
                    <span className={statusBadge(item.status)}>{statusLabel(item.status)}</span>
                  </div>
                  <p className="text-body-sm text-primary font-semibold mt-2">
                    {kitchenTerms.warehouseReturn.list.continueEdit}
                  </p>
                </button>
              ))}
            </div>
          )}

          {publishedReturns.length > 0 && (
            <div className="space-y-2">
              <p className="text-body-sm font-semibold text-on-surface-variant">
                {kitchenTerms.warehouseReturn.list.publishedSection}
              </p>
              {publishedReturns.map((item) => (
                <div key={item.id} className="card">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-body-lg font-semibold text-on-surface break-words">
                        {kitchenTerms.warehouseReturn.list.toWarehouse(item.warehouse_name)}
                      </p>
                      <p className="text-body-sm text-on-surface-variant mt-1">{summaryText(item.lines)}</p>
                      <p className="text-label-md font-mono text-on-surface-variant mt-1">{item.code}</p>
                    </div>
                    <span className={statusBadge(item.status)}>{statusLabel(item.status)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {returns.length === 0 && (
            <EmptyState
              icon="undo"
              title={kitchenTerms.warehouseReturn.list.emptyTitle}
              description={kitchenTerms.warehouseReturn.list.emptyDescription}
            />
          )}

          <button type="button" className="btn-primary w-full min-h-[44px] text-center" onClick={startNewReturn}>
            {kitchenTerms.warehouseReturn.list.newReturn}
          </button>
        </div>
      )}

      {!loading && !loadError && view === 'edit' && (
        <div className="space-y-4">
          <div className="card space-y-3">
            <p className="text-headline-md text-primary break-words">
              {kitchenTerms.warehouseReturn.editor.title(warehouseName)}
            </p>
            {returnCode ? (
              <p className="text-body-sm font-mono text-on-surface-variant">{returnCode}</p>
            ) : (
              <p className="text-body-sm text-on-surface-variant">Draft baru</p>
            )}

            <div className="space-y-2">
              <label className="field-label" htmlFor="wmr_notes">
                {kitchenTerms.warehouseReturn.editor.notesLabel}
              </label>
              <textarea
                id="wmr_notes"
                className="input-field min-h-[72px] resize-y"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={kitchenTerms.warehouseReturn.editor.notesPlaceholder}
              />
            </div>
          </div>

          <div className="card space-y-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-body-sm text-on-surface-variant">
                {kitchenTerms.warehouseReturn.editor.materialQtyLabel}
              </p>
              <button
                type="button"
                className="text-body-sm text-primary font-semibold shrink-0"
                onClick={() => setShowAllLines((prev) => !prev)}
              >
                {showAllLines
                  ? kitchenTerms.warehouseReturn.editor.hideZero
                  : kitchenTerms.warehouseReturn.editor.showAll}
              </button>
            </div>

            {visibleLines.length === 0 && (
              <p className="text-body-sm text-on-surface-variant">
                {kitchenTerms.warehouseReturn.editor.emptyMaterial}
              </p>
            )}

            {visibleLines.map((line) => {
              const realIdx = editorLines.findIndex(
                (l) => l.materialName === line.materialName && l.uom === line.uom,
              )
              return (
                <div
                  key={`${line.materialName}-${line.uom}`}
                  className="flex items-center justify-between gap-3 border-b border-surface-variant pb-3 last:border-0 last:pb-0"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-body-sm font-semibold text-on-surface break-words">{line.materialName}</p>
                    <p className="text-body-sm text-on-surface-variant">
                      {kitchenTerms.warehouseReturn.editor.maxUom(line.maxQty, line.uom)}
                    </p>
                  </div>
                  <QtyStepper
                    value={line.qty}
                    min={0}
                    max={line.maxQty}
                    disabled={line.maxQty <= 0 && line.qty <= 0}
                    onChange={(qty) => {
                      setEditorLines((prev) => prev.map((l, i) => (i === realIdx ? { ...l, qty } : l)))
                    }}
                  />
                </div>
              )
            })}
          </div>

          {formError && (
            <div role="alert" className="alert-error">
              <span>{formError}</span>
            </div>
          )}

          <StickyActionFooter>
            <button type="button" className="btn-outline flex-1 min-h-[44px]" onClick={() => setView('list')}>
              {kitchenTerms.warehouseReturn.editor.cancel}
            </button>
            <button
              type="button"
              className="btn-outline flex-1 min-h-[44px] disabled:opacity-40"
              disabled={isSaving}
              onClick={() => void handleSaveDraft()}
            >
              {kitchenTerms.warehouseReturn.editor.save}
            </button>
            <button type="button" className="btn-primary flex-1 min-h-[44px] text-center" onClick={openReview}>
              {kitchenTerms.warehouseReturn.editor.continue}
            </button>
          </StickyActionFooter>
        </div>
      )}

      {!loading && !loadError && view === 'review' && (
        <div className="space-y-4">
          <div className="card space-y-2">
            <p className="text-headline-md text-primary">{kitchenTerms.warehouseReturn.review.title}</p>
            <p className="text-body-sm text-on-surface-variant">
              {kitchenTerms.warehouseReturn.review.destination(warehouseName)}
            </p>
            {returnCode && (
              <p className="text-body-sm font-mono text-on-surface-variant">{returnCode}</p>
            )}
            {notes.trim() && (
              <p className="text-body-sm text-on-surface-variant">
                {kitchenTerms.warehouseReturn.review.notes(notes.trim())}
              </p>
            )}
          </div>

          <div className="card divide-y divide-surface-variant">
            {payloadLines.map((line, index) => (
              <div key={`${line.materialCode}-${index}`} className="flex items-center justify-between gap-3 p-3">
                <p className="text-body-sm font-semibold text-on-surface break-words">{line.materialCode}</p>
                <p className="text-body-lg font-bold text-primary shrink-0">
                  {line.qty} {line.uom}
                </p>
              </div>
            ))}
          </div>

          <div className="rounded-lg bg-surface-container p-3 flex items-center justify-between">
            <span className="text-body-sm text-on-surface-variant">
              {kitchenTerms.warehouseReturn.review.totalLabel}
            </span>
            <span className="text-body-lg font-bold text-on-surface">
              {kitchenTerms.warehouseReturn.review.totalValue(payloadLines.length, payloadTotalQty)}
            </span>
          </div>

          <StickyActionFooter>
            <button type="button" className="btn-outline flex-1 min-h-[44px]" onClick={() => setView('edit')}>
              {kitchenTerms.warehouseReturn.review.back}
            </button>
            <button
              type="button"
              className="btn-primary flex-1 min-h-[44px] text-center disabled:opacity-40"
              disabled={isPublishing}
              onClick={() => void handlePublish()}
            >
              {kitchenTerms.warehouseReturn.review.publish}
            </button>
          </StickyActionFooter>
        </div>
      )}

      {view === 'qr' && qrState && (
        <div className="space-y-4">
          <div className="card space-y-3 text-center">
            <p className="text-headline-md text-primary">{kitchenTerms.warehouseReturn.qr.title}</p>
            <p className="text-body-sm text-on-surface-variant">{kitchenTerms.warehouseReturn.qr.description}</p>
            <p className="text-body-sm text-on-surface-variant">
              {qrState.returnCode} · {qrState.warehouseName}
            </p>
          </div>

          <div className="card flex flex-col items-center gap-3 py-6">
            {!qrExpired ? (
              <QrDisplay token={qrState.payload} size="large" />
            ) : (
              <p className="text-body-lg font-semibold text-status-warning">
                {kitchenTerms.warehouseReturn.qr.expired}
              </p>
            )}
            <p className="text-body-sm font-semibold text-on-surface-variant">{qrTtlLabel}</p>
            <p className="text-body-sm font-mono text-on-surface-variant break-all text-center px-4">
              {qrState.payload}
            </p>
          </div>

          <button
            type="button"
            className="btn-primary w-full min-h-[44px]"
            onClick={() => {
              setQrState(null)
              setView('list')
            }}
          >
            {kitchenTerms.warehouseReturn.qr.done}
          </button>
        </div>
      )}

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}

      {!useApi && (
        <p className="text-caption text-on-surface-variant">{kitchenTerms.warehouseReturn.mockHint}</p>
      )}
    </section>
  )
}
